/**
 * useBasicInfoAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 2 ("Basic Info").
 * Mirrors useGoalAnalytics.js / useWelcomeAnalytics.js conventions.
 *
 * Privacy (Nutrition Policy v1.0 §19 Privacy & Data Minimization): raw
 * health answers — age, height, weight, target weight — must never be
 * sent to ordinary analytics events. The same rule covers the mobile
 * number and the email address, both direct personal contact data: neither
 * ever leaves the screen through an analytics event — only presence (and,
 * for mobile, country) does. This hook only ever forwards non-sensitive,
 * coarse metadata:
 *  - trackAgeBlocked takes a `reason` bucket ("under_13" / "under_18"),
 *    never the raw age value.
 *  - trackMobileCountryChange takes an ISO country code only.
 *  - trackStepNext takes an explicit whitelisted meta object (booleans /
 *    unit labels / country code only) rather than the raw form payload,
 *    so a future caller can't accidentally leak biometrics or a phone
 *    number by passing the whole payload through.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[BasicInfoAnalytics] ${eventName}`, "color:#2E7D32;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useBasicInfoAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("basic_info_step_view", { session_id: sessionId, step: 2 });
  }, []);

  const trackFieldError = useCallback((sessionId, field, reason) => {
    send("basic_info_field_error", { session_id: sessionId, field, reason });
  }, []);

  /**
   * @param {string} sessionId
   * @param {"under_13"|"under_18"} reason - coarse bucket only; never the
   *   user's raw age.
   */
  const trackAgeBlocked = useCallback((sessionId, reason) => {
    send("basic_info_age_blocked", { session_id: sessionId, reason });
  }, []);

  const trackGenderSelect = useCallback((sessionId, gender) => {
    send("basic_info_gender_select", { session_id: sessionId, gender });
  }, []);

  const trackUnitToggle = useCallback((sessionId, field, unit) => {
    send("basic_info_unit_toggle", { session_id: sessionId, field, unit });
  }, []);

  /**
   * @param {string} sessionId
   * @param {string} countryCode - ISO country code (e.g. "IN"). Never the
   *   phone number, and never a partial number.
   *
   * Useful for spotting geography mixes and for sizing SMS/OTP costs per
   * region, neither of which needs the subscriber digits.
   */
  const trackMobileCountryChange = useCallback((sessionId, countryCode) => {
    send("basic_info_mobile_country_change", {
      session_id: sessionId,
      country_code: countryCode,
    });
  }, []);

  /**
   * @param {string} sessionId
   * @param {object} meta
   * @param {boolean} meta.hasName
   * @param {boolean} meta.hasTargetWeight
   * @param {boolean} meta.hasMobile
   * @param {string}  meta.mobileCountry - ISO country code only.
   * @param {boolean} meta.hasEmail
   * @param {"cm"|"ft"} meta.heightUnit
   * @param {"kg"|"lb"} meta.weightUnit
   *
   * Deliberately does NOT accept an arbitrary payload — age, gender,
   * heightCm, weightKg, targetWeightKg, the email address itself, and any
   * form of the mobile number (mobileE164 / mobileNational) must never
   * reach this event.
   */
  const trackStepNext = useCallback(
    (
      sessionId,
      {
        hasName,
        hasTargetWeight,
        hasMobile,
        mobileCountry,
        hasEmail,
        heightUnit,
        weightUnit,
      } = {}
    ) => {
      send("basic_info_step_next", {
        session_id: sessionId,
        step: 2,
        hasName,
        hasTargetWeight,
        hasMobile,
        mobile_country: mobileCountry,
        hasEmail,
        heightUnit,
        weightUnit,
      });
    },
    []
  );

  const trackStepPrevious = useCallback((sessionId) => {
    send("basic_info_step_previous", { session_id: sessionId, step: 2 });
  }, []);

  return {
    trackStepView,
    trackFieldError,
    trackAgeBlocked,
    trackGenderSelect,
    trackUnitToggle,
    trackMobileCountryChange,
    trackStepNext,
    trackStepPrevious,
  };
};

export default useBasicInfoAnalytics;